import { useEffect, useState } from "react";
import _ from "lodash";

import {
    formatNumber,
    getSinglarOrPluralWord
} from "../../../../utilities/helpers";

import Card from "react-bootstrap/Card";
import Stack from "react-bootstrap/Stack";
import Button from "react-bootstrap/Button";

import { QuickpickBadge } from "../../NewSeatedPurchaseWrapper/NewSeatedPurchaseContainer/TicketSelectionPanel/ListingsContainer/TicketGroup/TicketGroup";

import placeholder from "../../../../assets/placeholder.png";
import GAPlaceholder from "../../../../assets/placeholder-ga.png";

import "./buyingThis.scss";

export default function BuyingThis({
    cart,
    event,
    getTicketImage,
    showBtn = false,
    handleClick,
}) {
    const [ticketGroups, setTicketGroups] = useState([]);

    useEffect(() => {
        if (cart?.tickets) {
            const updatedGroups = _.chain(cart.tickets)
                .groupBy((ticket) =>
                    `${ticket.sectionId}-${ticket.generalAdmission ? 'GA' : ticket.rowNumber}-${ticket.offer?.id}`
                )
                .map((tickets, key) => {
                    const firstTicket = tickets[0];
                    return {
                        GA: firstTicket.generalAdmission || false,
                        sectionId: firstTicket.sectionId,
                        sectionNumber: firstTicket.sectionNumber,
                        rowNumber: firstTicket.rowNumber,
                        tickets: tickets,
                        offer: firstTicket.offer,
                    };
                })
                .value();

            setTicketGroups(updatedGroups);
        }
    }, [cart, event]);

    if (!cart || !cart.tickets || cart.tickets.length === 0) {
        return null;
    }

    const getSeats = (seats) => {
        if (seats.length === 0) return "";
        seats.sort((a, b) => a - b);
        const ranges = [];
        let start = seats[0];
        let prev = seats[0];

        for (let i = 1; i <= seats.length; i++) {
            if (i === seats.length || seats[i] !== prev + 1) {
                ranges.push(start === prev ? start.toString() : `${start}-${prev}`);
                start = seats[i];
            }
            prev = seats[i];
        }

        // If there's only one range and it's a consecutive range (contains a hyphen)
        if (ranges.length === 1 && ranges[0].includes("-")) {
            return "Your seats are together 👥";
        }

        return `${getSinglarOrPluralWord(seats?.length, "Seat")}: ${ranges.join(
            ", "
        )}`;
    };

    if (cart?.package) {
        return (
            <div className='buying-this-container'>
                <SharedCard
                    data={cart}
                    getTicketImage={getTicketImage}
                    showBtn={showBtn}
                    handleClick={handleClick}
                    getSeats={getSeats}
                    isPackage={true}
                    venue={event?.venue}
                />
            </div>
        );
    }

    return (
        <div className='buying-this-container'>
            {ticketGroups.map((ticketGroup, i) => {
                return (
                    <SharedCard
                        key={i}
                        data={ticketGroup}
                        getTicketImage={getTicketImage}
                        showBtn={showBtn}
                        handleClick={handleClick}
                        getSeats={getSeats}
                        isPackage={false}
                        venue={event?.venue}
                    />
                );
            })}
        </div>
    );
}

const SharedCard = ({
    getTicketImage,
    showBtn,
    handleClick,
    getSeats,
    data,
    isPackage,
    venue
}) => {
    // User can only purchase season package seats from a single row/section at a time...
    const ticketInfo = isPackage
        ? {
            GA: data.tickets[0].generalAdmission,
            sectionId: data.tickets[0].sectionId,
            sectionNumber: data.tickets[0].sectionNumber,
            sectionName: data.tickets[0].sectionName ? data.tickets[0].sectionName.toLowerCase() : data.tickets[0].sectionNumber.toLowerCase(),
            rowNumber: data.tickets[0].rowNumber,
            tickets: data.tickets.filter(
                (ticket, index, self) =>
                    index === self.findIndex((t) => t.seatId === ticket.seatId)
            ),
            count: data.package.events.length,
            name: data.package?.name,
        }
        : {
            GA: data.GA,
            sectionId: data.sectionId,
            sectionNumber: data.sectionNumber,
            sectionName: data.sectionName,
            rowNumber: data.rowNumber,
            tickets: data.tickets,
            count: data.tickets?.length,
            name: data.offer?.name
        };

    const badgeProps = isPackage
        ? {
            isListing: false,
            listing: undefined,
            inventoryType: "open",
            offerName: ticketInfo.name,
        }
        : {
            isListing: Boolean(data.listing),
            listing: data.listing,
            inventoryType: data.offer?.inventoryType,
            offerName: ticketInfo.name,
        };

    const IconsToShow = () => {
        const tickets = ticketInfo.tickets;
        const hasGA = tickets.some(ticket => ticket.generalAdmission);
        const hasSeated = tickets.some(ticket => !ticket.generalAdmission);
        const hasAccessible = tickets.some(ticket => ticket.accessible);

        return (
            <div className=" d-flex flex-row gap-1 align-items-center">
                {hasSeated && (
                    <svg
                        width='24'
                        height='24'
                        viewBox='0 0 24 25'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                    >
                        <path
                            d='M12.6545 4.01172H12.3455C10.1124 4.01172 8.16364 5.92176 8.16364 8.4524V14.5371H16.8364V8.4524C16.8364 5.92176 14.8876 4.01172 12.6545 4.01172ZM6.16364 8.4524C6.16364 4.89531 8.93133 2.01172 12.3455 2.01172H12.6545C16.0687 2.01172 18.8364 4.89531 18.8364 8.4524V15.249C18.8364 15.5873 18.7112 15.8952 18.5064 16.125C18.2807 16.3785 17.9581 16.5371 17.6 16.5371H7.4C7.04189 16.5371 6.71935 16.3785 6.49356 16.125C6.28882 15.8952 6.16364 15.5873 6.16364 15.249V8.4524ZM20.8364 12.6264C20.6949 12.4379 20.4747 12.3168 20.2273 12.3168C19.8005 12.3168 19.4545 12.6773 19.4545 13.1219V19.2405C19.4545 19.6852 19.8005 20.0456 20.2273 20.0456C20.4265 20.0456 20.6082 19.967 20.7452 19.838C20.9017 19.6907 21 19.4776 21 19.2405V13.1219C21 12.935 20.9389 12.763 20.8364 12.6264ZM18.0182 20.9338C18.4265 20.6716 18.744 19.8055 18.8193 19.2405C18.8305 19.1566 18.8364 19.0794 18.8364 19.0117C18.8364 18.6995 18.7866 18.4872 18.6949 18.3469C18.4978 18.0456 18.107 18.0771 17.6 18.1654C17.5525 18.1737 17.504 18.1824 17.4545 18.1915C17.4327 18.1954 17.4107 18.1995 17.3885 18.2036C17.0279 18.2697 16.6195 18.3447 16.187 18.3447H8.81299C8.38046 18.3447 7.97214 18.2697 7.61151 18.2036C7.58931 18.1995 7.56729 18.1954 7.54545 18.1915C7.49599 18.1824 7.44749 18.1737 7.4 18.1654C6.893 18.0771 6.50221 18.0456 6.30512 18.3469C6.21341 18.4872 6.16364 18.6995 6.16364 19.0117C6.16364 19.0794 6.16948 19.1566 6.18066 19.2405C6.25596 19.8055 6.57355 20.6716 6.98179 20.9338C7.05939 20.9837 7.14026 21.0117 7.22338 21.0117H17.7766C17.8597 21.0117 17.9406 20.9837 18.0182 20.9338ZM16.8354 18.9763C16.8354 18.9763 16.8355 18.9774 16.8356 18.9799ZM8.16465 18.9763C8.16465 18.9763 8.16459 18.9775 8.16437 18.9799ZM4.2548 19.838C4.39184 19.967 4.57348 20.0456 4.77273 20.0456C5.19949 20.0456 5.54545 19.6852 5.54545 19.2405V13.1219C5.54545 12.6773 5.19949 12.3168 4.77273 12.3168C4.52532 12.3168 4.30507 12.4379 4.16364 12.6264C4.06111 12.763 4 12.935 4 13.1219V19.2405C4 19.4776 4.09833 19.6907 4.2548 19.838Z'
                            fill='#777E91'
                        />
                    </svg>
                )}
                {hasGA && (
                    <svg
                        width='24'
                        height='24'
                        viewBox='0 0 24 24'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                    >
                        <path
                            d='M7.91687 21.0337L8.37235 21.4892C9.03093 22.1477 10.0987 22.1477 10.7573 21.4892L21.4896 10.7568C22.1482 10.0982 22.1482 9.03046 21.4896 8.37187L21.0341 7.91639C20.5825 7.46471 19.9081 7.32564 19.2693 7.32842C18.6705 7.33102 17.9174 7.1846 17.3159 6.58315C16.7145 5.9817 16.5681 5.22858 16.5707 4.62979C16.5735 3.99102 16.4344 3.31663 15.9827 2.86495L15.5272 2.40947C14.8686 1.75088 13.8009 1.75088 13.1423 2.40947L2.40996 13.1418C1.75137 13.8004 1.75137 14.8682 2.40996 15.5268L2.86542 15.9822C3.31711 16.4339 3.9915 16.573 4.63027 16.5702C5.22906 16.5676 5.98218 16.714 6.58363 17.3155C7.18507 17.9169 7.3315 18.67 7.3289 19.2688C7.32612 19.9076 7.46518 20.582 7.91687 21.0337ZM14.3348 4.06931L4.0698 14.3343L4.2473 14.5118C4.24932 14.5124 4.25149 14.5131 4.25379 14.5138C4.31986 14.5335 4.44145 14.5541 4.62151 14.5533C5.52967 14.5494 6.89212 14.7717 8.00978 15.8893C9.12745 17.007 9.34972 18.3694 9.34577 19.2776C9.34499 19.4576 9.36563 19.5792 9.38532 19.6453C9.38601 19.6476 9.38667 19.6498 9.38731 19.6518L9.56483 19.8293L19.8298 9.56435L19.6523 9.38683C19.6503 9.3862 19.6481 9.38553 19.6458 9.38485C19.5797 9.36515 19.4581 9.34451 19.2781 9.34529C18.3699 9.34924 17.0075 9.12698 15.8898 8.00931C14.7721 6.89164 14.5499 5.52919 14.5538 4.62102C14.5546 4.44098 14.5339 4.31939 14.5143 4.25332C14.5136 4.25101 14.5129 4.24885 14.5123 4.24683L14.3348 4.06931Z'
                            fill='#777E91'
                        />
                    </svg>
                )}
                {hasAccessible && (
                    <svg
                        width='20'
                        height='20'
                        viewBox='0 0 20 20'
                        fill='none'
                        xmlns='http://www.w3.org/2000/svg'
                    >
                        <path
                            d='M7.54277 3.6482C8.50477 3.6482 9.27877 2.8662 9.27877 1.9242C9.27877 0.968195 8.50477 0.200195 7.54277 0.200195C6.58477 0.200195 5.80677 0.968195 5.80677 1.9242C5.80677 2.8662 6.58477 3.6482 7.54277 3.6482ZM17.7308 15.4422L14.7708 10.3682C14.5868 10.0622 14.2648 9.8902 13.9308 9.8802H9.58676L9.54277 8.3542H12.6968C13.1268 8.3282 13.4808 8.0042 13.4808 7.5662C13.4808 7.1342 13.1368 6.7982 12.6968 6.7842H9.45476L9.34277 5.5282C9.27877 4.6242 8.49476 3.9122 7.57477 3.9622C6.64677 4.0142 5.94677 4.8062 5.99077 5.7222L6.29676 10.9542C6.37277 11.8722 7.14476 12.5222 8.06076 12.5222H13.6908L15.9908 16.4762C16.2668 16.9242 16.9048 17.1082 17.3848 16.8222C17.8588 16.5342 18.0008 15.9422 17.7308 15.4422ZM8.65877 18.4442C5.81077 18.4442 3.50077 16.1562 3.50077 13.3242C3.50077 11.7742 4.20877 10.3902 5.31077 9.4482L5.21477 7.8482C3.37077 8.9922 2.13477 11.0022 2.13477 13.3242C2.13477 16.9002 5.05277 19.8022 8.65877 19.8022C11.3128 19.8022 13.5808 18.2162 14.6008 15.9602L13.6848 14.3802C13.1928 16.6942 11.1368 18.4442 8.65877 18.4442Z'
                            fill='#777E91'
                        />
                    </svg>
                )}
            </div>
        )
    }

    return (
        <Card className='card-xl card-xl--light buying-this'>
            <Card.Body className='d-flex-column gap-2'>
                <div className='d-flex col-divider'>
                    <div className='vjx-img-wrap'>
                        <img
                            src={ticketInfo?.GA ? GAPlaceholder : getTicketImage(venue?.slug, ticketInfo.sectionNumber.toLowerCase(), "thumbnail") || placeholder}
                            alt='Seat view for this ticket'
                        />
                    </div>
                    <div className='ticket-info'>
                        <div className='col'>
                            <Stack direction='horizontal' gap={2} className='flex-wrap'>
                                <QuickpickBadge {...badgeProps} />
                            </Stack>
                            <Stack className='gap-md-2 align-items-start mt-2'>
                                <Card.Title
                                    as='h5'
                                    className={`ticket-loc ticket-loc`}
                                >
                                    {IconsToShow()}
                                    {ticketInfo.GA
                                        ? `${ticketInfo.sectionNumber}`
                                        : `Sec ${ticketInfo.sectionNumber} • Row ${ticketInfo.rowNumber}`}
                                </Card.Title>

                                <div className='tablet-desktop-only small'>
                                    {ticketInfo.GA ? (
                                        <Card.Text>This area is standing or seat-yourself 🧑‍🤝‍🧑</Card.Text>
                                    ) : (
                                        <Card.Text>
                                            {getSeats(
                                                ticketInfo.tickets.map((t) => Number(t.seatNumber))
                                            )}
                                        </Card.Text>
                                    )}
                                </div>
                            </Stack>
                        </div>
                        <div className='tablet-desktop-only'>
                            <div className='right-side'>
                                <div className='ticket-count-info'>
                                    <span className='ticket-count'>
                                        {formatNumber(ticketInfo.count)}
                                    </span>
                                    <span className='ticket-text'>
                                        {getSinglarOrPluralWord(
                                            ticketInfo.count,
                                            isPackage ? "Event" : 'Ticket'
                                        )}
                                    </span>
                                </div>
                                {showBtn && (
                                    <Button
                                        variant='secondary'
                                        onClick={handleClick}
                                        className='mt-0'
                                    >
                                        Go to my wallet
                                    </Button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
                <Stack
                    direction='horizontal'
                    className='mb-1 justify-content-between align-items-center mobile-only small'
                >
                    {ticketInfo.GA ? (
                        <span>This is a standing area 🧑‍🤝‍🧑</span>
                    ) : (
                        <span>
                            {getSeats(ticketInfo.tickets.map((t) => Number(t.seatNumber)))}
                        </span>
                    )}
                    <span>
                        {formatNumber(ticketInfo.count)}{" "}
                        <span>
                            {getSinglarOrPluralWord(
                                ticketInfo.count,
                                isPackage ? "Event" : 'Ticket'
                            )}
                        </span>
                    </span>
                </Stack>
            </Card.Body>
        </Card>
    );
};
